import {
  MatterTreatmentAppointmentsCard,
  MatterTreatmentRecordsCard,
} from "@/components/features";
import {
  ActionConfirmationModal,
  Button,
  Card,
  CurrencyWrapper,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogPortal,
  DialogTitle,
  ServerErrorFallbackCard,
  Typography,
} from "@/components/ui";
import { MatterContactConnectionRichCard } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionRichCard";
import { MatterMedicalTreatmentContextMenu } from "@/components/features/matter/context-menus/MatterMedicalTreatmentContextMenu";
import { useUser } from "@/components/UserProvider";
import { useMedicalTreatmentBillViewModel } from "@/hooks/matter-view-models/use-medical-treatment-bill-view-model";
import { useMedicalTreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  retrieveContactQueryOptions,
  useCreateMatterUserConnection,
} from "@/lib/data/react-query";
import { useDeleteMedicalTreatment } from "@/lib/data/react-query/matters/medical-treatment";
import { useCreateMedicalTreatmentBalanceConfirmation } from "@/lib/data/react-query/matters/medical-treatment-balance-confirmation";
import { CONTACT_MEDICAL_PROVIDER_COMMUNICATION_LABELS } from "@/lib/data/static/contactStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MedicalTreatment } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS,
} from "@/lib/utils/contact-role-select-card-class";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import {
  getConnectionContactName,
  getConnectionPreviewContact,
} from "@/lib/utils/connections/item-contact-connections";
import {
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
  formatUsersName,
} from "@/lib/utils/format";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Activity,
  AlertCircle,
  Building2,
  CircleCheck,
  CircleX,
  Copy,
  Mail,
  Phone,
  PlusCircle,
  Siren,
  Stethoscope,
} from "lucide-react";
import { useState } from "react";
import { MedicalTreatmentDetailsModal } from "./MedicalTreatmentDetailsModal";

interface MedicalTreatmentProviderCardProps {
  treatment: MedicalTreatment;
  matter: Matter;
}

const MEDICAL_PROVIDER_COMMUNICATION_LABEL_SET = new Set<string>(
  CONTACT_MEDICAL_PROVIDER_COMMUNICATION_LABELS,
);

const COMMUNICATION_LABEL_DISPLAY: Record<string, string> = {
  "Medical Record Requests": "Records",
  "Billing Record Requests": "Billing",
  "Billing and Medical Record Requests": "Billing & medical records",
};

function getProviderIcon(types: string[] = []) {
  const joined = types.join(" ").toLowerCase();
  if (joined.includes("emergency") || joined.includes("ambulance"))
    return Siren;
  if (
    joined.includes("hospital") ||
    joined.includes("surgery") ||
    joined.includes("urgent care")
  )
    return Building2;
  if (
    joined.includes("physical therapy") ||
    joined.includes("rehabilitation") ||
    joined.includes("chiro")
  )
    return Activity;
  if (joined.includes("physician") || joined.includes("doctor"))
    return Stethoscope;
  return Stethoscope;
}

function MedicalTreatmentProviderCardDangerous({
  treatment,
  matter,
}: MedicalTreatmentProviderCardProps) {
  const { hasActionPermission, currentUser } = useUser();
  const {
    openTreatmentBalanceConfirmationModal,
    openTreatmentBalanceConfirmationHistoryModal,
  } = useGlobalModal();
  const createBalanceConfirmation =
    useCreateMedicalTreatmentBalanceConfirmation({});
  const deleteMedicalTreatment = useDeleteMedicalTreatment({
    successMessage: "Medical treatment deleted successfully",
  });
  const createMatterUserConnection = useCreateMatterUserConnection({
    showToast: false,
  });
  const hasMedicalTreatmentUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatment,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasMedicalTreatmentDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatment,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canViewBalanceHistory = Boolean(true);
  const treatmentTypes = treatment.type;

  const [isAppointmentsExpanded, setIsAppointmentsExpanded] = useState<
    "appointments" | "none"
  >("none");
  const [isRecordsExpanded, setIsRecordsExpanded] = useState<
    "records" | "none"
  >("none");
  const [isDeleteTreatmentOpen, setIsDeleteTreatmentOpen] = useState(false);
  const [isEditTreatmentDetailsOpen, setIsEditTreatmentDetailsOpen] =
    useState(false);
  const [isConfirmBalanceModalOpen, setIsConfirmBalanceModalOpen] =
    useState(false);
  const completeMedicalTreatments = useMedicalTreatmentViewModel(matter);
  const completeTreatment = completeMedicalTreatments.find(
    (t) => t.id === treatment.id,
  );

  const {
    totalBill,
    totalPaid,
    totalOutstanding,
    totalMedpayPaid,
    totalInsurancePaid,
    totalClientPaid,
  } = calculateMedicalTreatmentBillTotals(completeTreatment?.bills || []);
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(value);
  const balanceConfirmations =
    matter.medicalTreatmentBalanceConfirmations.filter(
      (confirmation) => confirmation.medicalTreatmentId === treatment.id,
    );
  const latestBalanceConfirmation = balanceConfirmations.length
    ? balanceConfirmations.slice().sort((a, b) => {
        const aDate = a.date ?? a.dateCreated;
        const bDate = b.date ?? b.dateCreated;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return dayjs(bDate).diff(dayjs(aDate));
      })[0]
    : null;
  const hasBalanceConfirmations = balanceConfirmations.length > 0;
  const isBalanceConfirmed = latestBalanceConfirmation?.confirmed === true;
  const confirmedBalanceAmount = latestBalanceConfirmation?.amount ?? null;
  const balanceDifference =
    isBalanceConfirmed && confirmedBalanceAmount != null
      ? confirmedBalanceAmount - totalBill
      : null;
  const hasBalanceDiscrepancy =
    balanceDifference != null && Math.abs(balanceDifference) >= 0.01;
  const latestConfirmationId = latestBalanceConfirmation?.id;
  const confirmedByConnection = latestConfirmationId
    ? matter.userConnections
        .filter(
          (connection) =>
            connection.medicalTreatmentBalanceConfirmationId ===
            latestConfirmationId,
        )
        .sort((a, b) => dayjs(b.dateCreated).diff(dayjs(a.dateCreated)))[0]
    : undefined;
  const confirmedByName = formatUsersName(confirmedByConnection)?.trim();
  const balanceStatusLabel = hasBalanceConfirmations
    ? hasBalanceDiscrepancy
      ? `Confirmed: ${balanceDifference >= 0 ? "+" : "-"}${formatCurrency(
          Math.abs(balanceDifference),
        )}`
      : isBalanceConfirmed
        ? "Confirmed"
        : "Not confirmed"
    : "Not confirmed";
  const balanceStatusToneClass = hasBalanceConfirmations
    ? isBalanceConfirmed
      ? hasBalanceDiscrepancy
        ? "text-amber-700"
        : "text-emerald-700"
      : "text-red-700"
    : "text-amber-700";
  const BalanceStatusIcon = !hasBalanceConfirmations
    ? CircleX
    : isBalanceConfirmed && !hasBalanceDiscrepancy
      ? CircleCheck
      : AlertCircle;
  const balanceStatusSubtext = latestBalanceConfirmation
    ? [
        latestBalanceConfirmation.date
          ? dayjs(latestBalanceConfirmation.date).format("MMM D, YYYY")
          : undefined,
        confirmedByName ? `by ${confirmedByName}` : undefined,
      ]
        .filter(Boolean)
        .join(" ")
    : "No confirmation on file";

  const treatmentLienAmount = calculateMedicalTreatmentBillTotals(
    (completeTreatment?.bills || []).filter(
      (bill) => bill.lien === "Lien Filed" || bill.lien === "Lien Signed",
    ),
  ).totalOutstanding;

  const medicalTreatmentBills = useMedicalTreatmentBillViewModel(
    matter.id,
    treatment.id,
  );

  const isConfirmActionLoading =
    createBalanceConfirmation.isPending || createMatterUserConnection.isPending;
  const currentUserName =
    formatUsersName(currentUser)?.trim() ?? "Current user";
  const provider = completeTreatment?.provider;
  const treatingPhysicianConnection = completeTreatment?.treatingPhysician;
  const { data: providerContactData } = useQuery({
    ...retrieveContactQueryOptions(provider?.targetObjectId),
    enabled: Boolean(provider?.targetObjectId),
  });
  const ProviderIcon = getProviderIcon(treatmentTypes);
  const providerContact =
    providerContactData?.data ?? getConnectionPreviewContact(provider);
  const providerPhones = providerContact?.phones ?? [];
  const providerEmails = providerContact?.emails ?? [];
  const generalPhones = providerPhones.filter((phone) => {
    const label = phone.label;
    return !label || !MEDICAL_PROVIDER_COMMUNICATION_LABEL_SET.has(label);
  });
  const generalEmails = providerEmails.filter((email) => {
    const label = email.label;
    return !label || !MEDICAL_PROVIDER_COMMUNICATION_LABEL_SET.has(label);
  });
  const communicationPhones: Record<string, string[]> = {};
  const communicationEmails: Record<string, string[]> = {};
  for (const phone of providerPhones) {
    const label = phone.label;
    if (!label || !MEDICAL_PROVIDER_COMMUNICATION_LABEL_SET.has(label))
      continue;
    communicationPhones[label] = communicationPhones[label] ?? [];
    communicationPhones[label].push(formatLocalOrIntlPhoneNumber(phone));
  }
  for (const email of providerEmails) {
    const label = email.label;
    if (!label || !MEDICAL_PROVIDER_COMMUNICATION_LABEL_SET.has(label))
      continue;
    communicationEmails[label] = communicationEmails[label] ?? [];
    communicationEmails[label].push(email.address ?? "");
  }
  const hasCommunicationInfo =
    Object.values(communicationPhones).some((v) => v.length > 0) ||
    Object.values(communicationEmails).some((v) => v.length > 0) ||
    Boolean(treatment.portalLink);
  const portalLink = treatment.portalLink;
  const balanceStatusDisplay = (
    <div className="flex flex-col items-end gap-1 text-right">
      <div className="flex items-center gap-2">
        <BalanceStatusIcon className={cn("h-4 w-4", balanceStatusToneClass)} />
        {canViewBalanceHistory && hasBalanceConfirmations ? (
          <Button
            type="button"
            variant="link"
            className={cn(
              "h-auto p-0 text-sm font-medium underline underline-offset-4",
              balanceStatusToneClass,
            )}
            onClick={() => {
              openTreatmentBalanceConfirmationHistoryModal({
                matterId: matter.id,
                treatmentId: treatment.id,
              });
            }}
          >
            {balanceStatusLabel}
          </Button>
        ) : hasMedicalTreatmentUpdatePermission && !hasBalanceConfirmations ? (
          <Button
            type="button"
            variant="link"
            className={cn(
              "h-auto p-0 text-sm font-medium underline underline-offset-4",
              balanceStatusToneClass,
            )}
            onClick={() => {
              setIsConfirmBalanceModalOpen(true);
            }}
          >
            {balanceStatusLabel}
          </Button>
        ) : (
          <p className={cn("text-sm font-medium", balanceStatusToneClass)}>
            {balanceStatusLabel}
          </p>
        )}
      </div>
      <p className="text-muted-foreground text-xs">{balanceStatusSubtext}</p>
    </div>
  );

  const recordContactFooter = hasCommunicationInfo ? (
    <div className="space-y-3">
      <p className="font-semibold text-white/90">Record contact</p>
      {portalLink ? (
        <div className="flex items-center gap-2">
          <span className="text-white/60">Portal:</span>
          <button
            type="button"
            onClick={() => void copyToClipboard(portalLink, "Portal link")}
            className="flex min-w-0 items-center gap-1 text-blue-200 hover:underline"
          >
            <span className="truncate">{portalLink}</span>
            <Copy className="h-3 w-3" />
          </button>
        </div>
      ) : null}
      {CONTACT_MEDICAL_PROVIDER_COMMUNICATION_LABELS.map((label) => {
        const phones = communicationPhones[label] ?? [];
        const emails = communicationEmails[label] ?? [];
        if (phones.length === 0 && emails.length === 0) return null;
        const displayLabel = COMMUNICATION_LABEL_DISPLAY[label] ?? label;
        return (
          <div key={label} className="space-y-1">
            <p className="font-medium text-white/70">{displayLabel}</p>
            {phones.map((phone, i) => (
              <div
                key={`p-${i}`}
                className="flex items-center gap-2 text-white/80"
              >
                <Phone className="h-3 w-3 shrink-0 text-white/60" />
                <span className="flex-1">{phone}</span>
                <button
                  type="button"
                  onClick={() => void copyToClipboard(phone, "Phone number")}
                  className="text-white/50 hover:text-white/90"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            ))}
            {emails.map((email, i) => (
              <div
                key={`e-${i}`}
                className="flex items-center gap-2 text-white/80"
              >
                <Mail className="h-3 w-3 shrink-0 text-white/60" />
                <span className="flex-1 truncate">{email}</span>
                <button
                  type="button"
                  onClick={() => void copyToClipboard(email, "Email")}
                  className="text-white/50 hover:text-white/90"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  ) : null;

  const lopValue = treatment.letterOfProtection;
  const lopDisplay = lopValue ? "Yes" : "No";
  const openTreatmentDetailsEditor = () => {
    setIsEditTreatmentDetailsOpen(true);
  };

  if (!completeTreatment) return null;

  return (
    <>
      <MatterMedicalTreatmentContextMenu
        matter={matter}
        treatment={treatment}
        providerName={formatContactsName(providerContact)}
        onEdit={
          hasMedicalTreatmentUpdatePermission
            ? openTreatmentDetailsEditor
            : undefined
        }
      >
        <Card className="border-border w-full overflow-hidden">
          <div className="flex min-h-0">
            {providerContact ? (
              <MatterContactConnectionRichCard
                contact={providerContact}
                matterContactConnection={provider}
                badges={treatmentTypes.length > 0 ? treatmentTypes : undefined}
                maxVisibleBadges={6}
                tone="provider"
                leadIcon={ProviderIcon}
                onEdit={openTreatmentDetailsEditor}
                showEditButton={hasMedicalTreatmentUpdatePermission}
                editPermissionOverride={hasMedicalTreatmentUpdatePermission}
                editAriaLabel="Edit medical treatment"
                editButtonTestId="edit-medical-treatment-button"
                detailPhones={generalPhones}
                detailEmails={generalEmails}
                expandedFooter={recordContactFooter}
              />
            ) : (
              <div className="border-border w-[380px] shrink-0 border-r p-6">
                <div
                  className={cn(
                    CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS,
                    "border-dashed",
                  )}
                >
                  {hasMedicalTreatmentUpdatePermission && (
                    <button
                      type="button"
                      className={CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS}
                      onClick={openTreatmentDetailsEditor}
                      aria-label="Select provider"
                    />
                  )}
                  <div className={CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS}>
                    <div className={CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS}>
                      <span className="bg-muted text-muted-foreground flex h-7 w-7 items-center justify-center rounded-full">
                        <PlusCircle className="h-3.5 w-3.5 shrink-0" />
                      </span>
                      <Typography className="text-foreground text-sm leading-tight font-medium">
                        Select provider
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Right panel */}
            <div className="flex min-w-0 flex-1 flex-col">
              {/* Metadata row */}
              <div className="border-border flex items-start gap-2 border-b p-4 text-sm">
                <div className="grid min-w-0 flex-1 grid-cols-4 gap-4">
                  <div>
                    <p className="text-muted-foreground mb-1 text-xs">
                      Treatment status
                    </p>
                    <p className="font-medium">{treatment.status || "-"}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1 text-xs">
                      Treating physician
                    </p>
                    <p className="font-medium">
                      {getConnectionContactName(
                        treatingPhysicianConnection?.contact,
                      ) ?? "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1 text-xs">
                      Account number
                    </p>
                    <p className="font-medium">
                      {treatment.patientAccountNumber || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1 text-xs">
                      Letter of protection
                    </p>
                    <p className="font-medium">{lopDisplay}</p>
                  </div>
                </div>
                <div className="border-border ml-auto border-l pl-4">
                  <p className="text-muted-foreground mb-1 text-xs">
                    Balance confirmation
                  </p>
                  {balanceStatusDisplay}
                </div>
              </div>

              {/* Financial summary */}
              <div className="mx-4 mt-3 mb-0">
                <div className="border-border bg-muted/30 rounded-lg border p-3">
                  <div className="grid grid-cols-7 gap-3">
                    <div>
                      <p className="text-muted-foreground text-xs">
                        Total billed
                      </p>
                      <p className="text-sm font-semibold">
                        <CurrencyWrapper value={totalBill} />
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        Client paid
                      </p>
                      <p className="text-sm font-semibold">
                        <CurrencyWrapper value={totalClientPaid} />
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        Insurance paid
                      </p>
                      <p className="text-sm font-semibold">
                        <CurrencyWrapper value={totalInsurancePaid} />
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        MedPay Paid
                      </p>
                      <p className="text-sm font-semibold">
                        <CurrencyWrapper value={totalMedpayPaid} />
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        Total paid
                      </p>
                      <p className="text-sm font-semibold">
                        <CurrencyWrapper value={totalPaid} />
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        Lien amount
                      </p>
                      <p className="text-sm font-semibold">
                        <CurrencyWrapper value={treatmentLienAmount} />
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">
                        Outstanding
                      </p>
                      <p className="text-sm font-semibold">
                        <CurrencyWrapper value={totalOutstanding} />
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Collapsibles */}
              <div className="mt-3 flex flex-col gap-2 px-4 pb-4">
                <MatterTreatmentAppointmentsCard
                  matterId={matter.id}
                  treatmentId={completeTreatment.id}
                  appointments={completeTreatment.appointments}
                  expanded={isAppointmentsExpanded}
                  setExpanded={setIsAppointmentsExpanded}
                />
                {(!completeTreatment.billNotApplicable ||
                  !completeTreatment.recordNotApplicable) && (
                  <MatterTreatmentRecordsCard
                    records={completeTreatment.records}
                    bills={medicalTreatmentBills}
                    expanded={isRecordsExpanded}
                    setExpanded={setIsRecordsExpanded}
                    matter={matter}
                    showBillingSection={!completeTreatment.billNotApplicable}
                    showMedicalSection={!completeTreatment.recordNotApplicable}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>
      </MatterMedicalTreatmentContextMenu>

      {/* Balance confirmation modal */}
      <Dialog
        open={isConfirmBalanceModalOpen}
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            setIsConfirmBalanceModalOpen(true);
            return;
          }
          if (!isConfirmActionLoading) {
            setIsConfirmBalanceModalOpen(false);
          }
        }}
      >
        <DialogPortal>
          <DialogContent className="bg-card z-[101] max-h-[70vh] overflow-y-auto">
            <DialogTitle>Confirm balance</DialogTitle>
            <DialogDescription className="space-y-3">
              <span className="text-muted-foreground block text-sm">
                By confirming, you acknowledge the balance below is accurate.
              </span>
              <div className="border-border grid gap-2 rounded-md border p-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Amount</span>
                  <span>{formatCurrency(totalBill)}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Date</span>
                  <span>{dayjs().format("MMM D, YYYY")}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Confirmed by</span>
                  <span>{currentUserName}</span>
                </div>
              </div>
            </DialogDescription>
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="outline" disabled={isConfirmActionLoading}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                variant="outline"
                disabled={isConfirmActionLoading}
                onClick={() => {
                  setIsConfirmBalanceModalOpen(false);
                  openTreatmentBalanceConfirmationModal({
                    matterId: matter.id,
                    treatmentId: treatment.id,
                    initialData: {
                      confirmed: true,
                      amount: totalBill,
                      date: dayjs().format("YYYY-MM-DD"),
                    },
                  });
                }}
              >
                Edit
              </Button>
              <DialogClose asChild>
                <Button
                  variant="default"
                  disabled={isConfirmActionLoading}
                  onClick={async () => {
                    const created = await createBalanceConfirmation.mutateAsync(
                      {
                        matterId: matter.id,
                        medicalTreatmentId: treatment.id,
                        confirmed: true,
                        amount: totalBill,
                        date: dayjs().format("YYYY-MM-DD"),
                      },
                    );
                    if (currentUser.id) {
                      await createMatterUserConnection.mutateAsync({
                        matterId: matter.id,
                        label: "Balance confirmed by",
                        medicalTreatmentBalanceConfirmationId: created.data.id,
                        userId: currentUser.id,
                      });
                    }
                  }}
                >
                  Confirm
                </Button>
              </DialogClose>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      {isEditTreatmentDetailsOpen && (
        <MedicalTreatmentDetailsModal
          treatment={completeTreatment}
          matterId={matter.id}
          setOpen={setIsEditTreatmentDetailsOpen}
          onDeleteTreatment={
            hasMedicalTreatmentDeletePermission
              ? () => {
                  setIsDeleteTreatmentOpen(true);
                }
              : undefined
          }
        />
      )}
      <ActionConfirmationModal
        open={isDeleteTreatmentOpen}
        setOpen={setIsDeleteTreatmentOpen}
        title="Delete medical treatment?"
        description="This action cannot be undone. This will delete the medical treatment and all associated treatment dates, bills, records, and plans."
        buttonText="Delete treatment"
        isActionPending={deleteMedicalTreatment.isPending}
        afterActionFn={async () => {
          await deleteMedicalTreatment.mutateAsync({
            matterId: matter.id,
            id: treatment.id,
          });
        }}
      />
    </>
  );
}

export const MedicalTreatmentProviderCard = (
  props: MedicalTreatmentProviderCardProps,
) => {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-4">
          <ServerErrorFallbackCard message="Unable to load medical treatment details." />
        </div>
      }
    >
      <MedicalTreatmentProviderCardDangerous {...props} />
    </ErrorBoundary>
  );
};
